import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup,
} from 'react-simple-maps';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { MapPin, Crosshair, X } from 'lucide-react';
import { toast } from 'sonner';
import { pinCodeToLatLng, STATE_CENTROIDS } from '../../data/indiaPincodeCoords';

const INDIA_TOPO_URL = '/maps/india.json';

const SOURCE_OPTIONS = ['', 'Manual', 'Website', 'Referral', 'Meta Campaign', 'Meta', 'Cold Call', 'Excel Import', 'Other'];

const GeoHeatmap = ({ api, period: parentPeriod }) => {
  const [hotspot, setHotspot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(parentPeriod || 'month');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([82, 23]); // India centroid
  const [activePin, setActivePin] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => { setPeriod(parentPeriod || 'month'); }, [parentPeriod]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ period });
        if (stateFilter) params.append('state', stateFilter);
        if (districtFilter) params.append('district', districtFilter);
        if (cityFilter) params.append('city', cityFilter);
        if (sourceFilter) params.append('source', sourceFilter);
        const r = await api.get(`/reports/geo-hotspot?${params.toString()}`);
        if (!cancelled) setHotspot(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load geo hotspot');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [api, period, stateFilter, districtFilter, cityFilter, sourceFilter]);

  // Resolve markers
  const markers = useMemo(() => {
    if (!hotspot?.byPinCode) return [];
    return hotspot.byPinCode
      .map((p) => {
        const coords = pinCodeToLatLng(p.pinCode, p.state);
        if (!coords) return null;
        // Deterministic jitter so two pins with the same 3-digit prefix
        // (e.g. 110001 vs 110024) become independently clickable.
        const last3 = parseInt((p.pinCode || '').toString().slice(-3), 10) || 0;
        const jitterLat = ((last3 % 17) - 8) * 0.012;
        const jitterLng = ((last3 % 11) - 5) * 0.012;
        return {
          ...p,
          lat: coords.lat + jitterLat,
          lng: coords.lng + jitterLng,
          resolvedCity: coords.city,
        };
      })
      .filter(Boolean);
  }, [hotspot]);

  const maxCount = useMemo(() => markers.reduce((m, x) => Math.max(m, x.total), 1), [markers]);
  const radiusFor = (count) => 4 + (count / maxCount) * 18;

  const handleStateFilterApply = (st) => {
    setStateFilter(st);
    setActivePin(null);
    setDrilldown(null);
    if (st && STATE_CENTROIDS[st]) {
      setCenter([STATE_CENTROIDS[st].lng, STATE_CENTROIDS[st].lat]);
      setZoom(3.5);
    } else {
      setCenter([82, 23]); setZoom(1);
    }
  };

  const handleMarkerClick = async (pin) => {
    setActivePin(pin.pinCode);
    setDrilldownLoading(true);
    setDrilldown(null);
    try {
      const r = await api.get(`/reports/geo-pincode/${pin.pinCode}?period=${period}`);
      setDrilldown(r.data);
    } catch (e) {
      toast.error('Failed to load pin code details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="geo-heatmap">
      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Period</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[130px]" data-testid="geo-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">7 days</SelectItem>
                  <SelectItem value="month">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">State</span>
              <Select value={stateFilter || 'all'} onValueChange={(v) => handleStateFilterApply(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="geo-state-filter"><SelectValue placeholder="All states" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  {Object.keys(STATE_CENTROIDS).sort().map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="District"
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              className="w-[140px]"
              data-testid="geo-district-filter"
            />
            <Input
              placeholder="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-[150px]"
              data-testid="geo-city-filter"
            />
            <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[170px]" data-testid="geo-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {SOURCE_OPTIONS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => { setZoom(1); setCenter([82, 23]); setStateFilter(''); setDistrictFilter(''); setCityFilter(''); setSourceFilter(''); setActivePin(null); setDrilldown(null); }} data-testid="geo-reset">
              <Crosshair className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Badge variant="outline" data-testid="geo-total-leads">{hotspot?.totalLeads ?? 0} leads</Badge>
            <Badge variant="outline">{markers.length} pin codes</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Map + Drilldown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="geo-map-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> India Geographical Hotspot</CardTitle>
            <CardDescription>Bubble size = lead volume · Click any pin code for stage-wise drill-down</CardDescription>
          </CardHeader>
          <CardContent style={{ height: 500 }} className="p-0 overflow-hidden">
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 1000 }}
              style={{ width: '100%', height: '100%', background: '#f8fafc' }}
            >
              <ZoomableGroup center={center} zoom={zoom} onMoveEnd={({ coordinates, zoom: z }) => { setCenter(coordinates); setZoom(z); }}>
                <Geographies geography={INDIA_TOPO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#E5E7EB"
                        stroke="#FFFFFF"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: '#FCA5A5', outline: 'none', cursor: 'pointer' },
                          pressed: { outline: 'none' },
                        }}
                        onClick={() => {
                          const name = geo.properties?.st_nm || geo.properties?.name;
                          if (name) handleStateFilterApply(name);
                        }}
                      />
                    ))
                  }
                </Geographies>
                {markers.map((m) => (
                  <Marker key={m.pinCode} coordinates={[m.lng, m.lat]} onClick={() => handleMarkerClick(m)}>
                    <circle
                      r={radiusFor(m.total)}
                      fill={activePin === m.pinCode ? '#DC2626' : '#E31E24'}
                      fillOpacity={activePin === m.pinCode ? 0.95 : 0.6}
                      stroke="#fff"
                      strokeWidth={1.2}
                      style={{ cursor: 'pointer' }}
                      data-testid={`geo-pin-${m.pinCode}`}
                    />
                    {radiusFor(m.total) > 12 && (
                      <text textAnchor="middle" y={3} style={{ fontSize: 9, fontWeight: 600, fill: '#fff', pointerEvents: 'none' }}>{m.total}</text>
                    )}
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
          </CardContent>
        </Card>

        {/* Drilldown panel */}
        <Card data-testid="geo-drilldown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Pin Code Detail</span>
              {drilldown && <Button variant="ghost" size="icon" onClick={() => { setDrilldown(null); setActivePin(null); }}><X className="h-4 w-4" /></Button>}
            </CardTitle>
            <CardDescription>{drilldown ? `PIN ${drilldown.pinCode} · ${drilldown.total} leads` : 'Click a pin on the map'}</CardDescription>
          </CardHeader>
          <CardContent>
            {drilldownLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!drilldownLoading && !drilldown && !loading && (
              <div className="space-y-2 text-xs text-muted-foreground">
                <p>Top states by leads:</p>
                {(hotspot?.byState || []).slice(0, 8).map((s) => (
                  <div key={s.state} className="flex items-center justify-between text-foreground">
                    <button className="text-left hover:underline" onClick={() => handleStateFilterApply(s.state)} data-testid={`geo-state-row-${s.state.replace(/\s+/g, '-')}`}>{s.state}</button>
                    <Badge variant="outline">{s.total}</Badge>
                  </div>
                ))}
              </div>
            )}
            {drilldown && (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Stages</p>
                  <div className="space-y-1">
                    {Object.entries(drilldown.stages || {}).map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between text-xs">
                        <span>{stage}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sources</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(drilldown.sources || {}).map(([src, count]) => (
                      <Badge key={src} variant="secondary" className="text-[10px]">{src}: {count}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Recent leads</p>
                  <div className="space-y-1 max-h-44 overflow-auto">
                    {(drilldown.leads || []).slice(0, 12).map((l) => (
                      <div key={l.id} className="text-xs border rounded px-2 py-1 flex items-center justify-between">
                        <span className="truncate">{l.name} · {l.mobile}</span>
                        <Badge variant="outline" className="text-[10px] ml-2">{l.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* State summary table */}
      {hotspot?.byState?.length > 0 && (
        <Card data-testid="geo-state-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">State-wise Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {hotspot.byState.slice(0, 12).map((s) => (
                <div key={s.state} className="border rounded p-2 text-xs">
                  <p className="font-medium">{s.state}</p>
                  <p className="text-muted-foreground">{s.total} leads · {s.uniquePinCodes} pins · {s.closed} closed</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* District summary (Phase 6) */}
      {hotspot?.byDistrict?.length > 0 && (
        <Card data-testid="geo-district-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">District-wise Summary</CardTitle>
            <CardDescription>Auto-derived from Pin Code · click to filter</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {hotspot.byDistrict.slice(0, 16).map((d, idx) => (
                <button
                  key={`${d.state}-${d.district}-${idx}`}
                  className="border rounded p-2 text-xs text-left hover:bg-muted/50 transition"
                  onClick={() => { setDistrictFilter(d.district); setStateFilter(d.state); }}
                  data-testid={`geo-district-row-${(d.district || 'unknown').toString().replace(/\s+/g, '-')}`}
                >
                  <p className="font-medium">{d.district}</p>
                  <p className="text-muted-foreground">{d.state} · {d.total} leads · {d.closed} closed</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* City summary (Phase 6) */}
      {hotspot?.byCity?.length > 0 && (
        <Card data-testid="geo-city-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">City-wise Summary</CardTitle>
            <CardDescription>Auto-derived from Pin Code</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {hotspot.byCity.slice(0, 16).map((c, idx) => (
                <div key={`${c.state}-${c.district}-${c.city}-${idx}`} className="border rounded p-2 text-xs">
                  <p className="font-medium">{c.city}</p>
                  <p className="text-muted-foreground">{c.district}, {c.state} · {c.total} leads</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GeoHeatmap;
